import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import UnifiedSidebar from './UnifiedSidebar';
import ToolPanel from './ToolPanel';
import AtlasChat from './atlas/AtlasChat';
import FileLibraryModal from './FileLibraryModal';
import SettingsModal from './SettingsModal';
import ReleaseNotesModal from './ReleaseNotesModal';
import CompanyWorkspaceIntroModal from './CompanyWorkspaceIntroModal';
import { auth } from '../firebase';
import {
  fetchAtlasMcpConnector,
  fetchAtlasUserPreferences,
  updateAtlasMcpConnector,
  updateAtlasUserPreferences,
  updateConversation,
} from './atlas/atlasApi';
import { ATLAS_PLAYWRIGHT_AUTH_DISABLED, getPlaywrightIdToken } from '../devAuth';
import { formatAdminClientOptionLabel } from '../utils/adminClientGroups';
import './MainLayout.css';

const ATLAS_AGENT_API_URL = process.env.REACT_APP_ATLAS_AGENT_API_URL;
const DEFAULT_ATLAS_USER_PREFERENCES = {
  start_atlas_in_company_mode: false,
  prompt_on_new_company_chat: true,
  use_personal_memories_in_company_mode: false,
  company_workspace_intro_seen: false,
  persistence_available: false,
};
const DEFAULT_ATLAS_MCP_CONNECTOR = {
  name: 'user-mcp',
  url: '',
  enabled: false,
  persistence_available: false,
};
const SETTINGS_DEEP_LINK_SECTIONS = new Set(['user', 'atlas', 'connectors', 'billing', 'coverage']);

function getSettingsDeepLink(search = '') {
  const requested = new URLSearchParams(search).get('settings');
  return SETTINGS_DEEP_LINK_SECTIONS.has(requested) ? requested : null;
}

function normalizeAtlasUserPreferences(raw = {}) {
  const promptOnNewCompanyChat = raw.prompt_on_new_company_chat != null
    ? Boolean(raw.prompt_on_new_company_chat)
    : !Boolean(raw.company_conversation_confirm_dismissed);
  return {
    ...DEFAULT_ATLAS_USER_PREFERENCES,
    ...raw,
    start_atlas_in_company_mode: Boolean(raw.start_atlas_in_company_mode),
    prompt_on_new_company_chat: promptOnNewCompanyChat,
    use_personal_memories_in_company_mode: Boolean(raw.use_personal_memories_in_company_mode),
    company_workspace_intro_seen: Boolean(raw.company_workspace_intro_seen),
    company_conversation_confirm_dismissed: !promptOnNewCompanyChat,
    persistence_available: Boolean(raw.persistence_available),
  };
}

function normalizeAtlasMcpConnector(raw = {}) {
  return {
    ...DEFAULT_ATLAS_MCP_CONNECTOR,
    ...raw,
    name: 'user-mcp',
    url: String(raw?.url || '').trim(),
    enabled: Boolean(raw?.enabled && raw?.url),
    persistence_available: Boolean(raw?.persistence_available),
  };
}

// Hook to detect mobile
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return isMobile;
}

function formatRelativeTime(isoString) {
  if (!isoString) return '';
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return '';
  const diffMs = Date.now() - d.getTime();
  const diffSec = Math.max(0, Math.floor(diffMs / 1000));
  if (diffSec < 60) return 'Just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 14) return `${diffDay}d`;
  return d.toLocaleDateString();
}

async function getIdToken() {
  if (ATLAS_PLAYWRIGHT_AUTH_DISABLED) {
    return getPlaywrightIdToken();
  }
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  return user.getIdToken();
}

const MainLayout = ({
  user,
  clients,
  selectedClient,
  onClientChange,
  coverageState,
  isBootstrapLoading,
  selectedZip,
  onZipChange,
  onCoverageUpdate,
  onCompanySetupComplete,
  onSeatRequest,
  onReconcilePendingCheckouts,
  onRefreshCoverageContext,
  onRefreshClients,
  onLogout
}) => {
  const isMobile = useIsMobile();
  const { conversationId: urlConversationId } = useParams();
  const location = useLocation();
  const settingsDeepLink = useMemo(() => getSettingsDeepLink(location.search), [location.search]);
  const isAdmin = Boolean(user?.isAdmin);
  const actorUserId = user?.id != null ? String(user.id) : null;
  const clientUserId = selectedClient != null ? String(selectedClient) : null;
  const hasClientContext = Boolean(isAdmin && actorUserId && clientUserId && clientUserId !== actorUserId);

  // Sidebar state - on mobile, sidebar is hidden by default (overlay mode)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Brief fullscreen state for mobile
  const [briefFullscreenMobile, setBriefFullscreenMobile] = useState(false);

  // Chat scope for admin
  const [chatScope, setChatScope] = useState(() => (hasClientContext ? 'client' : 'me'));
  const [clientWriteEnabled, setClientWriteEnabled] = useState(false);
  const [workspaceScope, setWorkspaceScopeState] = useState('personal');
  const [workspaceCompanyId, setWorkspaceCompanyIdState] = useState(() => {
    try {
      return window.localStorage.getItem('atlasWorkspaceCompanyId') || null;
    } catch {
      return null;
    }
  });

  // Conversations state
  const [conversations, setConversations] = useState([]);
  const [conversationsStatus, setConversationsStatus] = useState('idle');
  const [conversationsError, setConversationsError] = useState('');
  const [hasMoreConversations, setHasMoreConversations] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [conversationId, setConversationId] = useState(urlConversationId || null);
  const [newChatFocusSignal, setNewChatFocusSignal] = useState(0);
  
  // Tool state
  const [selectedTool, setSelectedTool] = useState(null);

  const [settingsOpen, setSettingsOpen] = useState(Boolean(settingsDeepLink));
  const [settingsInitialSection, setSettingsInitialSection] = useState(settingsDeepLink || 'user');
  const [atlasUserPreferences, setAtlasUserPreferences] = useState(DEFAULT_ATLAS_USER_PREFERENCES);
  const [atlasUserPreferencesLoaded, setAtlasUserPreferencesLoaded] = useState(false);
  const [atlasUserPreferencesSaving, setAtlasUserPreferencesSaving] = useState(false);
  const [atlasMcpConnector, setAtlasMcpConnector] = useState(DEFAULT_ATLAS_MCP_CONNECTOR);
  const [atlasMcpConnectorSaving, setAtlasMcpConnectorSaving] = useState(false);
  const companyStartPreferenceAppliedRef = useRef(false);

  // File browser modal
  const [fileBrowserOpen, setFileBrowserOpen] = useState(false);
  const [dataCoverageLoading, setDataCoverageLoading] = useState(false);
  const [releaseNotesOpenKey, setReleaseNotesOpenKey] = useState(0);
  const [companyWorkspaceIntroOpen, setCompanyWorkspaceIntroOpen] = useState(false);
  const [companyWorkspaceIntroSource, setCompanyWorkspaceIntroSource] = useState('auto');
  const companyWorkspaceIntroAutoShownRef = useRef(false);

  useEffect(() => {
    if (!settingsDeepLink) return;
    setSettingsInitialSection(settingsDeepLink);
    setSettingsOpen(true);
  }, [settingsDeepLink]);

  // Ref bridge: File Browser → AtlasChat (attach file to input area)
  const attachFileRef = useRef(null);
  // Ref bridge: deleted chat → AtlasChat (drop its cached messages/streams)
  const forgetConversationRef = useRef(null);

  // Reference for owner user ID
  const readOwnerUserIdRef = useRef(null);

  const dataUserId = useMemo(() => {
    if (!isAdmin || !clientUserId) return null;
    const n = Number(clientUserId);
    return Number.isFinite(n) ? n : null;
  }, [isAdmin, clientUserId]);

  const coverageMatchesSelectedClient = useMemo(() => {
    if (!hasClientContext) return true;
    const coverageDataUserId = coverageState?.data_user_id != null ? String(coverageState.data_user_id) : null;
    return Boolean(coverageDataUserId && coverageDataUserId === clientUserId);
  }, [clientUserId, coverageState?.data_user_id, hasClientContext]);

  const companyMemberships = useMemo(
    () => (
      coverageMatchesSelectedClient && Array.isArray(coverageState?.company_memberships)
        ? coverageState.company_memberships
        : []
    ),
    [coverageMatchesSelectedClient, coverageState]
  );

  const selectedClientRecord = useMemo(() => {
    if (!selectedClient || !Array.isArray(clients)) return null;
    return clients.find((client) => String(client?.id) === String(selectedClient)) || null;
  }, [clients, selectedClient]);

  const selectedClientCompanyMemberships = useMemo(
    () => Array.isArray(selectedClientRecord?.company_memberships) ? selectedClientRecord.company_memberships : [],
    [selectedClientRecord]
  );

  const workspaceCompanyOptions = useMemo(() => {
    const byId = new Map();
    const scopedCompanyOptions = hasClientContext
      ? (companyMemberships.length > 0 ? companyMemberships : selectedClientCompanyMemberships)
      : companyMemberships;

    scopedCompanyOptions.forEach((company) => {
      const rawCompanyId = company?.company_id;
      if (rawCompanyId == null || rawCompanyId === '') return;
      const companyId = String(rawCompanyId);
      if (!byId.has(companyId)) {
        byId.set(companyId, {
          ...company,
          company_id: companyId,
        });
      }
    });
    return Array.from(byId.values());
  }, [companyMemberships, hasClientContext, selectedClientCompanyMemberships]);

  const selectedCompanyId = useMemo(() => {
    const hasStoredCompany = workspaceCompanyId
      && workspaceCompanyOptions.some((company) => String(company?.company_id) === String(workspaceCompanyId));
    const bootstrapCompanyId = coverageMatchesSelectedClient
      ? (coverageState?.selected_company_id || coverageState?.company_id)
      : null;
    const raw = hasStoredCompany
      ? workspaceCompanyId
      : bootstrapCompanyId || workspaceCompanyOptions[0]?.company_id || null;
    return raw != null && raw !== '' ? String(raw) : null;
  }, [coverageMatchesSelectedClient, coverageState, workspaceCompanyId, workspaceCompanyOptions]);

  const selectedCompany = useMemo(() => {
    if (!selectedCompanyId) return null;
    return workspaceCompanyOptions.find((company) => String(company?.company_id) === String(selectedCompanyId)) || null;
  }, [selectedCompanyId, workspaceCompanyOptions]);

  const companyName = selectedCompany?.company_display_name
    || selectedCompany?.display_name
    || selectedCompany?.company_name
    || (coverageMatchesSelectedClient ? coverageState?.company_display_name : null)
    || (coverageMatchesSelectedClient ? coverageState?.display_name : null)
    || (coverageMatchesSelectedClient ? coverageState?.company_name : null)
    || 'Company';
  const hasCompanyWorkspace = Boolean(
    selectedCompanyId
    && ((coverageMatchesSelectedClient && coverageState?.has_company_membership !== false) || workspaceCompanyOptions.length > 0)
  );
  const isCompanyScope = workspaceScope === 'company' && hasCompanyWorkspace;
  const workspaceContext = useMemo(() => ({
    scopeType: isCompanyScope ? 'company' : 'user',
    mode: isCompanyScope ? 'company' : 'personal',
    isCompanyScope,
    companyId: isCompanyScope ? selectedCompanyId : null,
    availableCompanyId: selectedCompanyId,
    companyName,
    companyOptions: workspaceCompanyOptions,
  }), [companyName, isCompanyScope, selectedCompanyId, workspaceCompanyOptions]);

  const setWorkspaceScope = useCallback((nextScope) => {
    const normalized = nextScope === 'company' ? 'company' : 'personal';
    setWorkspaceScopeState(normalized);
    try {
      window.localStorage.setItem('atlasWorkspaceScope', normalized);
    } catch {
      // localStorage may be unavailable in private contexts.
    }
  }, []);

  const handleWorkspaceScopeChange = useCallback((nextScope) => {
    if (hasClientContext) {
      setChatScope('client');
    }
    setWorkspaceScope(nextScope);
  }, [hasClientContext, setWorkspaceScope]);

  const setWorkspaceCompanyId = useCallback((companyId) => {
    const normalized = companyId != null && companyId !== '' ? String(companyId) : null;
    setWorkspaceCompanyIdState(normalized);
    try {
      if (normalized) {
        window.localStorage.setItem('atlasWorkspaceCompanyId', normalized);
      } else {
        window.localStorage.removeItem('atlasWorkspaceCompanyId');
      }
    } catch {
      // localStorage may be unavailable in private contexts.
    }
  }, []);

  useEffect(() => {
    companyStartPreferenceAppliedRef.current = false;
  }, [actorUserId]);

  useEffect(() => {
    try {
      window.localStorage.removeItem('atlasWorkspaceDefaultScope');
    } catch {
      // localStorage may be unavailable in private contexts.
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    setAtlasUserPreferencesLoaded(false);
    if (ATLAS_PLAYWRIGHT_AUTH_DISABLED) {
      setAtlasUserPreferences(DEFAULT_ATLAS_USER_PREFERENCES);
      setAtlasUserPreferencesLoaded(true);
      return () => {
        cancelled = true;
      };
    }

    fetchAtlasUserPreferences()
      .then((prefs) => {
        if (cancelled) return;
        setAtlasUserPreferences(normalizeAtlasUserPreferences(prefs));
      })
      .catch((error) => {
        console.warn('Failed to fetch Atlas user preferences:', error);
        if (!cancelled) {
          setAtlasUserPreferences(DEFAULT_ATLAS_USER_PREFERENCES);
        }
      })
      .finally(() => {
        if (!cancelled) setAtlasUserPreferencesLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [actorUserId]);

  useEffect(() => {
    let cancelled = false;
    const storageKey = `atlasMcpConnector:${actorUserId || 'local'}`;
    const loadLocal = () => {
      try {
        const saved = JSON.parse(window.localStorage.getItem(storageKey) || '{}');
        return normalizeAtlasMcpConnector({ ...saved, persistence_available: false });
      } catch {
        return DEFAULT_ATLAS_MCP_CONNECTOR;
      }
    };

    if (ATLAS_PLAYWRIGHT_AUTH_DISABLED) {
      setAtlasMcpConnector(loadLocal());
      return () => { cancelled = true; };
    }

    fetchAtlasMcpConnector()
      .then((connector) => {
        if (cancelled) return;
        setAtlasMcpConnector(
          connector?.persistence_available
            ? normalizeAtlasMcpConnector(connector)
            : loadLocal()
        );
      })
      .catch(() => {
        if (!cancelled) setAtlasMcpConnector(loadLocal());
      });
    return () => { cancelled = true; };
  }, [actorUserId]);

  const handleUpdateAtlasUserPreferences = useCallback(async (patch) => {
    if (ATLAS_PLAYWRIGHT_AUTH_DISABLED) {
      const normalized = normalizeAtlasUserPreferences({ ...atlasUserPreferences, ...patch });
      setAtlasUserPreferences(normalized);
      return normalized;
    }

    setAtlasUserPreferencesSaving(true);
    try {
      const saved = await updateAtlasUserPreferences(patch);
      const normalized = normalizeAtlasUserPreferences(saved);
      setAtlasUserPreferences(normalized);
      return normalized;
    } finally {
      setAtlasUserPreferencesSaving(false);
    }
  }, [atlasUserPreferences]);

  const handleUpdateAtlasMcpConnector = useCallback(async (connector) => {
    const normalized = normalizeAtlasMcpConnector(connector);
    const storageKey = `atlasMcpConnector:${actorUserId || 'local'}`;
    const saveLocally = () => {
      const localConnector = { ...normalized, persistence_available: false };
      window.localStorage.setItem(storageKey, JSON.stringify({
        url: localConnector.url,
        enabled: localConnector.enabled,
      }));
      setAtlasMcpConnector(localConnector);
      return localConnector;
    };

    if (ATLAS_PLAYWRIGHT_AUTH_DISABLED) return saveLocally();

    setAtlasMcpConnectorSaving(true);
    try {
      const saved = await updateAtlasMcpConnector(normalized);
      const next = normalizeAtlasMcpConnector(saved);
      setAtlasMcpConnector(next);
      window.localStorage.removeItem(storageKey);
      return next;
    } catch (error) {
      if (error?.response?.status === 503) return saveLocally();
      throw error;
    } finally {
      setAtlasMcpConnectorSaving(false);
    }
  }, [actorUserId]);

  const openCompanyWorkspaceGuide = useCallback(() => {
    setCompanyWorkspaceIntroSource('manual');
    setCompanyWorkspaceIntroOpen(true);
  }, []);

  const handleCompanyWorkspaceIntroDismiss = useCallback(async () => {
    setCompanyWorkspaceIntroOpen(false);
    if (
      companyWorkspaceIntroSource !== 'auto'
      || atlasUserPreferences.company_workspace_intro_seen
      || !atlasUserPreferences.persistence_available
    ) {
      return;
    }
    try {
      await handleUpdateAtlasUserPreferences({ company_workspace_intro_seen: true });
    } catch (error) {
      console.warn('Failed to save company workspace guide acknowledgement:', error);
    }
  }, [
    atlasUserPreferences.company_workspace_intro_seen,
    atlasUserPreferences.persistence_available,
    companyWorkspaceIntroSource,
    handleUpdateAtlasUserPreferences,
  ]);

  const handleWorkspaceCompanyChange = useCallback((companyId) => {
    if (companyId == null || companyId === '') return;
    const normalized = String(companyId);
    if (hasClientContext) {
      setChatScope('client');
    }
    setWorkspaceCompanyId(normalized);
    setWorkspaceScope('company');
    if (typeof onRefreshCoverageContext === 'function') {
      onRefreshCoverageContext(isAdmin && selectedClient ? selectedClient : null, { companyId: normalized })
        .catch((error) => console.error('Failed to refresh company workspace context:', error));
    }
  }, [hasClientContext, isAdmin, onRefreshCoverageContext, selectedClient, setWorkspaceCompanyId, setWorkspaceScope]);

  useEffect(() => {
    if (!atlasUserPreferencesLoaded || companyStartPreferenceAppliedRef.current) return;
    if (!atlasUserPreferences.start_atlas_in_company_mode) {
      companyStartPreferenceAppliedRef.current = true;
      return;
    }
    if (!hasCompanyWorkspace) return;
    const companyId = selectedCompanyId || workspaceCompanyOptions[0]?.company_id;
    if (!companyId) return;
    setWorkspaceCompanyId(companyId);
    setWorkspaceScope('company');
    companyStartPreferenceAppliedRef.current = true;
  }, [
    atlasUserPreferences.start_atlas_in_company_mode,
    atlasUserPreferencesLoaded,
    hasCompanyWorkspace,
    selectedCompanyId,
    setWorkspaceCompanyId,
    setWorkspaceScope,
    workspaceCompanyOptions,
  ]);

  useEffect(() => {
    if (hasClientContext) {
      setChatScope('client');
    } else {
      setChatScope('me');
    }
  }, [clientUserId, hasClientContext]);

  useEffect(() => {
    if (!workspaceCompanyId || workspaceCompanyOptions.length === 0) return;
    const stillAvailable = workspaceCompanyOptions.some(
      (company) => String(company?.company_id) === String(workspaceCompanyId)
    );
    if (!stillAvailable) {
      setWorkspaceCompanyId(null);
    }
  }, [setWorkspaceCompanyId, workspaceCompanyId, workspaceCompanyOptions]);

  useEffect(() => {
    if (!hasCompanyWorkspace && workspaceScope === 'company') {
      setWorkspaceScope('personal');
    }
  }, [hasCompanyWorkspace, setWorkspaceScope, workspaceScope]);

  useEffect(() => {
    if (!atlasUserPreferencesLoaded) return;
    if (!workspaceContext.isCompanyScope) return;
    if (atlasUserPreferences.company_workspace_intro_seen) return;
    if (companyWorkspaceIntroAutoShownRef.current) return;
    companyWorkspaceIntroAutoShownRef.current = true;
    setCompanyWorkspaceIntroSource('auto');
    setCompanyWorkspaceIntroOpen(true);
  }, [
    atlasUserPreferences.company_workspace_intro_seen,
    atlasUserPreferencesLoaded,
    workspaceContext.isCompanyScope,
  ]);

  // Sync conversationId state → URL (using replaceState to avoid React Router remount)
  useEffect(() => {
    const currentUrlCid = window.location.pathname.startsWith('/c/')
      ? window.location.pathname.split('/c/')[1]
      : null;
    if (conversationId && conversationId !== currentUrlCid) {
      window.history.replaceState(null, '', `/c/${conversationId}`);
    } else if (!conversationId && currentUrlCid) {
      window.history.replaceState(null, '', '/');
    }
  }, [conversationId]);

  // Sync browser back/forward → conversationId state
  useEffect(() => {
    const handlePopState = () => {
      const cid = window.location.pathname.startsWith('/c/')
        ? window.location.pathname.split('/c/')[1]
        : null;
      setConversationId(cid || null);
      if (!cid) setSelectedTool(null);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Keep the ref in sync before child effects run so direct /c/:id loads use the selected owner.
  const currentReadOwnerUserId = (hasClientContext || chatScope === 'client') ? clientUserId : null;
  readOwnerUserIdRef.current = currentReadOwnerUserId;

  // Update owner-dependent state when the admin is viewing another user's workspace.
  useEffect(() => {
    if (!currentReadOwnerUserId) setClientWriteEnabled(false);
  }, [currentReadOwnerUserId]);

  const conversationScopeKey = useMemo(() => {
    const ownerKey = hasClientContext ? `client:${clientUserId || ''}` : `user:${actorUserId || ''}`;
    const workspaceKey = workspaceContext.isCompanyScope
      ? `company:${workspaceContext.companyId || ''}`
      : 'personal';
    return `${ownerKey}:${workspaceKey}`;
  }, [actorUserId, clientUserId, hasClientContext, workspaceContext.companyId, workspaceContext.isCompanyScope]);
  const conversationScopeKeyRef = useRef(conversationScopeKey);

  useEffect(() => {
    conversationScopeKeyRef.current = conversationScopeKey;
  }, [conversationScopeKey]);

  // Fetch conversations (first page)
  // Rename / pin a chat. Updates in place rather than refetching so the list
  // doesn't collapse back to page one and lose whatever the user paged in.
  // The local re-sort mirrors the server's ORDER BY (pinned first, newest pin
  // first, then recency) so the optimistic order matches the next real fetch.
  const handleUpdateConversation = useCallback(async (conversationId, patch) => {
    const ownerUserId = hasClientContext ? clientUserId : readOwnerUserIdRef.current;
    const result = await updateConversation({ conversationId, ...patch, ownerUserId });

    setConversations((prev) => {
      const next = prev.map((c) => {
        if (c.conversation_id !== conversationId) return c;
        const merged = { ...c };
        if (patch.title !== undefined) {
          merged.title = result?.title ?? patch.title;
          merged.title_is_custom = true;
        }
        if (patch.pinned !== undefined) {
          merged.pinned = patch.pinned;
          merged.pinned_at = patch.pinned ? new Date().toISOString() : null;
        }
        return merged;
      });

      return next.sort((a, b) => {
        if (!!a.pinned !== !!b.pinned) return a.pinned ? -1 : 1;
        if (a.pinned && b.pinned) {
          return String(b.pinned_at || '').localeCompare(String(a.pinned_at || ''));
        }
        // Must be updated_at, matching the server's ORDER BY. The sidebar
        // *displays* last_message_at, but sorting by the displayed field would
        // let the optimistic order drift from what the next fetch returns.
        return String(b.updated_at || '').localeCompare(String(a.updated_at || ''));
      });
    });

    return result;
  }, [clientUserId, hasClientContext]);

  const refreshConversations = useCallback(async () => {
    if (!ATLAS_AGENT_API_URL) return;
    const requestScopeKey = conversationScopeKey;
    setConversationsError('');
    setConversationsStatus('loading');
    try {
      const idToken = await getIdToken();
      const url = new URL(`${ATLAS_AGENT_API_URL}/v1/conversations`);
      const ownerUserId = hasClientContext ? clientUserId : readOwnerUserIdRef.current;
      if (ownerUserId) url.searchParams.set('owner_user_id', String(ownerUserId));
      if (workspaceContext.isCompanyScope && workspaceContext.companyId) {
        url.searchParams.set('scope_type', 'company');
        url.searchParams.set('company_id', String(workspaceContext.companyId));
      } else {
        url.searchParams.set('scope_type', 'user');
      }
      url.searchParams.set('limit', '12');
      url.searchParams.set('offset', '0');
      const res = await fetch(url.toString(), {
        method: 'GET',
        headers: { Authorization: `Bearer ${idToken}` }
      });
      if (res.status === 501) {
        if (conversationScopeKeyRef.current !== requestScopeKey) return;
        setConversationsStatus('disabled');
        setConversations([]);
        setHasMoreConversations(false);
        return;
      }
      if (!res.ok) throw new Error(`conversations fetch failed: ${res.status}`);
      const data = await res.json();
      if (conversationScopeKeyRef.current !== requestScopeKey) return;
      const items = Array.isArray(data?.conversations) ? data.conversations : [];
      setConversations(items);
      setHasMoreConversations(!!data.has_more);
      setConversationsStatus('ready');
    } catch (e) {
      if (conversationScopeKeyRef.current !== requestScopeKey) return;
      setConversationsStatus('error');
      setConversationsError(e?.message || String(e));
    }
  }, [clientUserId, conversationScopeKey, hasClientContext, workspaceContext.companyId, workspaceContext.isCompanyScope]);

  // Load next page of conversations
  const loadMoreConversations = useCallback(async () => {
    if (!ATLAS_AGENT_API_URL || loadingMore || !hasMoreConversations) return;
    setLoadingMore(true);
    try {
      const idToken = await getIdToken();
      const url = new URL(`${ATLAS_AGENT_API_URL}/v1/conversations`);
      const ownerUserId = hasClientContext ? clientUserId : readOwnerUserIdRef.current;
      if (ownerUserId) url.searchParams.set('owner_user_id', String(ownerUserId));
      if (workspaceContext.isCompanyScope && workspaceContext.companyId) {
        url.searchParams.set('scope_type', 'company');
        url.searchParams.set('company_id', String(workspaceContext.companyId));
      } else {
        url.searchParams.set('scope_type', 'user');
      }
      url.searchParams.set('limit', '12');
      url.searchParams.set('offset', String(conversations.length));
      const res = await fetch(url.toString(), {
        method: 'GET',
        headers: { Authorization: `Bearer ${idToken}` }
      });
      if (!res.ok) throw new Error(`load more failed: ${res.status}`);
      const data = await res.json();
      const items = Array.isArray(data?.conversations) ? data.conversations : [];
      setConversations(prev => [...prev, ...items]);
      setHasMoreConversations(!!data.has_more);
    } catch (e) {
      console.error('Failed to load more conversations:', e);
    } finally {
      setLoadingMore(false);
    }
  }, [clientUserId, hasClientContext, loadingMore, hasMoreConversations, conversations.length, workspaceContext.companyId, workspaceContext.isCompanyScope]);

  useEffect(() => {
    refreshConversations();
  }, [refreshConversations]);

  // Refresh conversations when scope or client changes
  // Only clear conversationId on intentional switches (not initial value settling)
  const prevChatScopeRef = useRef(chatScope);
  const prevClientUserIdRef = useRef(clientUserId);
  const prevWorkspaceModeRef = useRef(workspaceContext.mode);
  const prevWorkspaceCompanyIdRef = useRef(workspaceContext.companyId);
  useEffect(() => {
    refreshConversations();
    const scopeChanged = prevChatScopeRef.current !== chatScope;
    const clientChanged = prevClientUserIdRef.current !== clientUserId && prevClientUserIdRef.current != null;
    const workspaceChanged = prevWorkspaceModeRef.current !== workspaceContext.mode
      || prevWorkspaceCompanyIdRef.current !== workspaceContext.companyId;
    prevChatScopeRef.current = chatScope;
    prevClientUserIdRef.current = clientUserId;
    prevWorkspaceModeRef.current = workspaceContext.mode;
    prevWorkspaceCompanyIdRef.current = workspaceContext.companyId;
    if (scopeChanged || clientChanged || workspaceChanged) {
      setConversationId(null);
      setSelectedTool(null);
    }
  }, [chatScope, clientUserId, refreshConversations, workspaceContext.mode, workspaceContext.companyId]);

  // Load conversation
  const loadConversation = useCallback((conv) => {
    const cid = typeof conv === 'string' ? conv : conv?.conversation_id;
    if (!cid) return;
    setConversationId(cid);
    // Clear tool selection when loading a conversation
    setSelectedTool(null);
  }, []);

  // Start new conversation
  const startNewConversation = useCallback(() => {
    setConversationId(null);
    setSelectedTool(null);
    setNewChatFocusSignal(signal => signal + 1);
  }, []);

  // Handle tool selection
  const handleToolChange = useCallback((toolId) => {
    setSelectedTool(toolId);
    // The mobile drawer overlays the content; close it so the tool is visible.
    setMobileSidebarOpen(false);
  }, []);

  // Close tool panel
  const closeToolPanel = useCallback(() => {
    setSelectedTool(null);
  }, []);

  // A Land Evaluation handed to the chat from the tool panel. The panel replaces the chat,
  // so the attachment waits here until AtlasChat mounts and consumes it.
  const [chatHandoff, setChatHandoff] = useState(null);
  const handleDiscussWithAtlas = useCallback((attachment) => {
    setChatHandoff(attachment);
    setSelectedTool(null);
  }, []);
  const handleChatHandoffConsumed = useCallback(() => setChatHandoff(null), []);

  // Close mobile sidebar when loading a conversation
  const handleLoadConversation = useCallback((conv) => {
    loadConversation(conv);
    setMobileSidebarOpen(false);
  }, [loadConversation]);

  const handleStartNewConversation = useCallback(() => {
    startNewConversation();
    setMobileSidebarOpen(false);
  }, [startNewConversation]);

  // A chat was deleted (or was already gone). Filtering in place keeps the
  // pages already loaded; the next page's offset shrinks by one, matching the
  // server's list, which no longer has it either. No app-wide toast exists, so
  // the sidebar row disappearing is the confirmation.
  const handleConversationDeleted = useCallback((cid) => {
    if (!cid) return;
    setConversations((prev) => prev.filter((c) => c.conversation_id !== cid));
    forgetConversationRef.current?.(cid);
    if (cid === conversationId) handleStartNewConversation();
  }, [conversationId, handleStartNewConversation]);

  // Library files attached from the File Browser while a tool panel had the
  // chat unmounted. AtlasChat adds them to the composer when it mounts.
  const [pendingLibraryAttachments, setPendingLibraryAttachments] = useState([]);
  const pendingLibraryAttachmentsRef = useRef([]);
  const handlePendingLibraryAttachmentsConsumed = useCallback(() => {
    pendingLibraryAttachmentsRef.current = [];
    setPendingLibraryAttachments([]);
  }, []);

  // Attach a file from the File Browser to the chat composer. Returns
  // { status: 'attached' | 'duplicate' | 'queued' | 'error', error? } so the
  // browser only reports success when the file really landed.
  // Last read-only state AtlasChat reported, for when it is not mounted.
  const chatReadOnlyRef = useRef({ conversationId: null, readOnly: false });
  const handleChatReadOnlyChange = useCallback((info) => { chatReadOnlyRef.current = info; }, []);

  const handleAttachFile = useCallback((file) => {
    if (!file?.file_id) return { status: 'error', error: 'This file cannot be attached.' };
    if (!selectedTool && typeof attachFileRef.current === 'function') {
      return attachFileRef.current(file) || { status: 'attached' };
    }
    const lastChat = chatReadOnlyRef.current;
    if (lastChat.readOnly && (lastChat.conversationId || null) === (conversationId || null)) {
      return { status: 'error', error: "This conversation is read-only, so files can't be attached." };
    }
    // The chat is not mounted (a tool panel replaced it): queue the file and
    // bring the chat back so it can pick it up.
    setSelectedTool(null);
    if (pendingLibraryAttachmentsRef.current.some((f) => f.file_id === file.file_id)) {
      return { status: 'duplicate' };
    }
    pendingLibraryAttachmentsRef.current = [...pendingLibraryAttachmentsRef.current, file];
    setPendingLibraryAttachments(pendingLibraryAttachmentsRef.current);
    return { status: 'queued' };
  }, [conversationId, selectedTool]);

  const handleOpenConversationFromFiles = useCallback((cid) => {
    if (!cid) return;
    setFileBrowserOpen(false);
    loadConversation(cid);
    setMobileSidebarOpen(false);
  }, [loadConversation]);

  const handleOpenFileBrowser = useCallback(() => {
    setFileBrowserOpen(true);
  }, []);

  const handleOpenDataCoverage = useCallback(() => {
    setSettingsInitialSection('coverage');
    setSettingsOpen(true);
    if (!onRefreshCoverageContext && !onReconcilePendingCheckouts) {
      return;
    }

    const targetClientId = isAdmin ? selectedClient : null;
    setDataCoverageLoading(true);
    Promise.resolve()
      .then(async () => {
        let bootstrapPayload = null;
        if (onRefreshCoverageContext) {
          bootstrapPayload = await onRefreshCoverageContext(targetClientId);
        }
        if (onReconcilePendingCheckouts) {
          await onReconcilePendingCheckouts({
            clientId: targetClientId,
            companyId: bootstrapPayload?.selected_company_id || bootstrapPayload?.company_id || coverageState?.selected_company_id || coverageState?.company_id || null,
            force: true,
          });
        }
      })
      .catch((error) => {
        console.error('Failed to prepare coverage context before opening modal:', error);
      })
      .finally(() => {
        setDataCoverageLoading(false);
      });
  }, [coverageState, isAdmin, onReconcilePendingCheckouts, onRefreshCoverageContext, selectedClient]);

  const handleOpenSettings = useCallback((section = 'user') => {
    setSettingsInitialSection(section);
    setSettingsOpen(true);
  }, []);

  const handleOpenReleaseNotes = useCallback(() => {
    setReleaseNotesOpenKey((key) => key + 1);
  }, []);

  const expectedCoverageUserId = useMemo(() => {
    if (isAdmin && selectedClient != null) {
      return String(selectedClient);
    }
    if (user?.id != null) {
      return String(user.id);
    }
    return null;
  }, [isAdmin, selectedClient, user]);

  useEffect(() => {
    const allowedTools = new Set(['research', 'land']);
    if (selectedTool && !allowedTools.has(selectedTool)) {
      setSelectedTool(null);
    }
  }, [selectedTool]);

  return (
    <div className={`main-layout ${isMobile ? 'main-layout-mobile' : ''} ${briefFullscreenMobile ? 'brief-fullscreen-mobile' : ''} ${workspaceContext.isCompanyScope ? 'workspace-company-mode' : ''} ${hasClientContext ? 'admin-client-profile-mode' : ''}`}>
      {/* Mobile overlay backdrop */}
      {isMobile && mobileSidebarOpen && (
        <div 
          className="mobile-sidebar-backdrop" 
          onClick={() => setMobileSidebarOpen(false)}
          aria-hidden="true"
        />
      )}
      
      <UnifiedSidebar
        // Chat history props
        conversations={conversations}
        conversationsStatus={conversationsStatus}
        conversationsError={conversationsError}
        hasMoreConversations={hasMoreConversations}
        loadingMore={loadingMore}
        onLoadMore={loadMoreConversations}
        conversationId={conversationId}
        loadConversation={handleLoadConversation}
        onUpdateConversation={handleUpdateConversation}
        onConversationDeleted={handleConversationDeleted}
        startNewConversation={handleStartNewConversation}
        formatRelativeTime={formatRelativeTime}
        // Admin props
        hasClientContext={hasClientContext}
        clients={clients}
        selectedClient={selectedClient}
        onClientChange={onClientChange}
        onRefreshClients={onRefreshClients}
        searchOwnerUserId={hasClientContext ? clientUserId : null}
        workspaceContext={workspaceContext}
        workspaceScope={workspaceScope}
        onWorkspaceScopeChange={handleWorkspaceScopeChange}
        onWorkspaceCompanyChange={handleWorkspaceCompanyChange}
        hasCompanyWorkspace={hasCompanyWorkspace}
        workspaceCompanyOptions={workspaceCompanyOptions}
        selectedWorkspaceCompanyId={selectedCompanyId}
        // Tools props
        user={user}
        selectedTool={selectedTool}
        onToolChange={handleToolChange}
        // Collapse state
        collapsed={sidebarCollapsed}
        onCollapse={setSidebarCollapsed}
        // Mobile state
        isMobile={isMobile}
        mobileOpen={mobileSidebarOpen}
        onMobileClose={() => setMobileSidebarOpen(false)}
        // User menu
        onLogout={onLogout}
        onOpenFileBrowser={handleOpenFileBrowser}
        onOpenDataCoverage={handleOpenDataCoverage}
        onOpenReleaseNotes={handleOpenReleaseNotes}
        onOpenUserSettings={() => handleOpenSettings('user')}
        atlasUserPreferences={atlasUserPreferences}
        onUpdateAtlasUserPreferences={handleUpdateAtlasUserPreferences}
      />

      <div className="main-layout-content">
        {selectedTool ? (
          <ToolPanel
            user={user}
            selectedTool={selectedTool}
            onClose={closeToolPanel}
            onDiscussWithAtlas={handleDiscussWithAtlas}
            isMobile={isMobile}
            onMenuClick={() => setMobileSidebarOpen(true)}
          />
        ) : (
          <AtlasChat
            user={user}
            selectedClient={selectedClient}
            conversationId={conversationId}
            setConversationId={setConversationId}
            refreshConversations={refreshConversations}
            conversations={conversations}
            conversationsStatus={conversationsStatus}
            chatScope={chatScope}
            clientWriteEnabled={clientWriteEnabled}
            hasClientContext={hasClientContext}
            dataUserId={dataUserId}
            readOwnerUserIdRef={readOwnerUserIdRef}
            readOwnerUserId={currentReadOwnerUserId}
            clientUserId={clientUserId}
            workspaceContext={workspaceContext}
            newChatFocusSignal={newChatFocusSignal}
            atlasUserPreferences={atlasUserPreferences}
            atlasUserPreferencesLoaded={atlasUserPreferencesLoaded}
            atlasMcpConnector={atlasMcpConnector}
            onUpdateAtlasUserPreferences={handleUpdateAtlasUserPreferences}
            // Mobile props
            isMobile={isMobile}
            onMenuClick={() => setMobileSidebarOpen(true)}
            briefFullscreenMobile={briefFullscreenMobile}
            setBriefFullscreenMobile={setBriefFullscreenMobile}
            attachFileRef={attachFileRef}
            forgetConversationRef={forgetConversationRef}
            chatHandoff={chatHandoff}
            onChatHandoffConsumed={handleChatHandoffConsumed}
            pendingLibraryAttachments={pendingLibraryAttachments}
            onPendingLibraryAttachmentsConsumed={handlePendingLibraryAttachmentsConsumed}
            onChatReadOnlyChange={handleChatReadOnlyChange}
          />
        )}
      </div>

      <FileLibraryModal
        isOpen={fileBrowserOpen}
        onClose={() => setFileBrowserOpen(false)}
        user={user}
        dataUserId={dataUserId}
        viewingUserLabel={hasClientContext
          ? (selectedClientRecord ? formatAdminClientOptionLabel(selectedClientRecord) : `user ${clientUserId}`)
          : null}
        workspaceContext={workspaceContext}
        onAttachFile={handleAttachFile}
        onOpenConversation={handleOpenConversationFromFiles}
        atlasUserPreferences={atlasUserPreferences}
        atlasUserPreferencesSaving={atlasUserPreferencesSaving}
        onUpdateAtlasUserPreferences={handleUpdateAtlasUserPreferences}
      />

      <SettingsModal
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        initialSection={settingsInitialSection}
        user={user}
        selectedClient={selectedClient}
        clients={clients}
        isAdmin={isAdmin}
        workspaceContext={workspaceContext}
        atlasUserPreferences={atlasUserPreferences}
        atlasUserPreferencesSaving={atlasUserPreferencesSaving}
        onUpdateAtlasUserPreferences={handleUpdateAtlasUserPreferences}
        atlasMcpConnector={atlasMcpConnector}
        atlasMcpConnectorSaving={atlasMcpConnectorSaving}
        onUpdateAtlasMcpConnector={handleUpdateAtlasMcpConnector}
        coverageState={coverageState}
        coverageLoading={Boolean(dataCoverageLoading || isBootstrapLoading)}
        expectedCoverageUserId={expectedCoverageUserId}
        selectedZip={selectedZip}
        onSelectZip={onZipChange}
        onCoverageUpdate={onCoverageUpdate}
        onCompanySetupComplete={onCompanySetupComplete}
        onSeatRequest={onSeatRequest}
        onPrepareCoverage={handleOpenDataCoverage}
      />

      <ReleaseNotesModal
        user={user}
        openKey={releaseNotesOpenKey}
        onOpenCompanyWorkspaceGuide={openCompanyWorkspaceGuide}
      />

      <CompanyWorkspaceIntroModal
        isOpen={companyWorkspaceIntroOpen}
        companyName={companyName}
        onClose={handleCompanyWorkspaceIntroDismiss}
        onDone={handleCompanyWorkspaceIntroDismiss}
      />

    </div>
  );
};

export default MainLayout;
